import { useMemo } from 'react'

import {
  ListPackagesQuery,
  useListPackagesQuery
} from 'src/config/generated/graphql'

// Re-export typed convenience aliases. Codegen treats Sanity enum scalars as
// `any`; narrowing them here so consumers get autocomplete + exhaustiveness
// without scattered `as` casts.
export type PackageSlug = 'kakano' | 'rakau' | 'tipu' | 'ngahere'
export type PackageColor = 'lavender' | 'cream' | 'lime' | 'espresso'
export type Cadence = 'monthly' | 'annual'

export type PackageRow = ListPackagesQuery['Package'][number]
export type PackageProductPrice = NonNullable<PackageRow['MonthlyProductPrice']>

// Slider min/max + per-tree price derived from a ProductPrice row.
// `max` is `undefined` when the underlying ProductPrice has `VolumeMax = NULL`
// (open-ended top tier — Ngahere annual).
export type SliderConfig = {
  pricePerTree: number
  min: number
  max?: number
}

const buildSliderConfig = (
  price: PackageProductPrice | null | undefined
): SliderConfig | undefined => {
  if (!price || price.VolumeMin == null) return undefined
  return {
    pricePerTree: price.Amount,
    min: price.Amount * price.VolumeMin,
    max: price.VolumeMax != null ? price.Amount * price.VolumeMax : undefined
  }
}

export const usePackages = () => {
  const { data, loading, error } = useListPackagesQuery({
    fetchPolicy: 'cache-first'
  })

  const packages = useMemo<PackageRow[]>(
    () => data?.Package ?? [],
    [data?.Package]
  )

  const findBySlug = (slug?: string) =>
    slug ? packages.find(p => p.Slug === slug) : undefined

  const getSliderConfig = (
    slug?: string,
    cadence: Cadence = 'monthly'
  ): SliderConfig | undefined => {
    const pkg = findBySlug(slug)
    if (!pkg) return undefined
    return buildSliderConfig(
      cadence === 'monthly' ? pkg.MonthlyProductPrice : pkg.AnnualProductPrice
    )
  }

  return {
    packages,
    loading,
    error,
    findBySlug,
    getSliderConfig
  }
}
